import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import isNaN from 'lodash-es/isNaN';
import isNumber from 'lodash-es/isNumber';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import Icon from '@panneau/element-icon';
import TextField from '@panneau/field-text';

var styles = {"container":"panneau-field-number-container","arrow":"panneau-field-number-arrow","dataListItems":"panneau-field-number-dataListItems","dataListItem":"panneau-field-number-dataListItem","dataListItemButton":"panneau-field-number-dataListItemButton"};

var _excluded = ["value", "step", "floatStep", "float", "dataList", "autoComplete", "disabled", "className", "onChange"];
var propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  step: PropTypes.number,
  floatStep: PropTypes.number,
  "float": PropTypes.bool,
  dataList: PropTypes.arrayOf(PropTypes.number),
  autoComplete: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func
};
var defaultProps = {
  value: null,
  step: 1,
  floatStep: 0.1,
  "float": false,
  dataList: null,
  autoComplete: false,
  disabled: false,
  className: null,
  onChange: null
};
function isNumeric(str) {
  if (typeof str !== 'string') return false; // we only process strings!
  return !isNaN(str) &&
  // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
  !isNaN(parseFloat(str));
}
var NumberField = function NumberField(_ref) {
  var value = _ref.value,
    step = _ref.step,
    floatStep = _ref.floatStep,
    _float = _ref["float"],
    dataList = _ref.dataList,
    autoComplete = _ref.autoComplete,
    disabled = _ref.disabled,
    className = _ref.className,
    onChange = _ref.onChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var parseValue = useCallback(function (newValue) {
    if (newValue !== null) {
      if (_float) {
        return newValue;
      }
      if (isNumber(newValue)) {
        return newValue;
      }
      if (isNumeric(newValue)) {
        return _float ? parseFloat(newValue) : parseInt(newValue, 10);
      }
    }
    return null;
  }, [_float]);
  var onInputChange = useCallback(function (val) {
    if (onChange !== null) {
      if (_float) {
        onChange(val !== null && val.length > 0 ? val : null);
      } else {
        onChange(val !== null && val.length > 0 ? parseValue(val) : null);
      }
      onChange(val !== null && val.length > 0 ? parseValue(val) : null);
    }
  }, [onChange, _float]);

  // Datalist

  var hasDataList = dataList !== null;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dataListActive = _useState2[0],
    setDataListActive = _useState2[1];
  var onInputFocus = useCallback(function () {
    if (hasDataList) {
      setDataListActive(true);
    }
  }, [setDataListActive, hasDataList]);
  var onInputBlur = useCallback(function () {
    if (hasDataList && dataListActive) {
      setDataListActive(false);
    }
  }, [setDataListActive, hasDataList, dataListActive]);
  var onDataListClick = useCallback(function (dataListValue) {
    if (onChange !== null && dataListValue !== null) {
      onChange(parseValue(dataListValue));
      setDataListActive(false);
    }
  }, [onChange, setDataListActive]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)])
  }, /*#__PURE__*/React.createElement(TextField, Object.assign({
    type: "number",
    className: styles.input,
    value: value !== null ? "".concat(value) : '',
    step: _float ? floatStep : step,
    autoComplete: autoComplete ? 'on' : 'off',
    onChange: onInputChange,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    disabled: disabled
  }, props)), !disabled && hasDataList ? /*#__PURE__*/React.createElement("div", {
    className: styles.arrow
  }, /*#__PURE__*/React.createElement(Icon, {
    name: dataListActive ? 'caret-up' : 'caret-down'
  })) : null, !disabled && hasDataList && dataListActive ? /*#__PURE__*/React.createElement("ul", {
    className: styles.dataListItems
  }, dataList.map(function (dataListValue) {
    return /*#__PURE__*/React.createElement("li", {
      key: "data-list-".concat(dataListValue),
      className: styles.dataListItem
    }, /*#__PURE__*/React.createElement("button", {
      className: styles.dataListItemButton,
      type: "button",
      onTouchStart: function onTouchStart() {
        onDataListClick(dataListValue);
      },
      onMouseDown: function onMouseDown() {
        onDataListClick(dataListValue);
      }
    }, dataListValue));
  })) : null);
};
NumberField.propTypes = propTypes;
NumberField.defaultProps = defaultProps;

var definition = {
  id: 'number',
  component: 'Number'
};

export { NumberField as default, definition };
